import { useEffect, useState, useCallback } from "react";
import { ListChecks, Edit, Trash, Filter } from "lucide-react";
import { Select } from "antd";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { DialogModal, ConfirmPopupModal } from "../../components/ui/dialogModal";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_KPI_METRICS, EMPLOYEE_DEPARTMENT } from "../../constants/api";

import { useAppraisalPermissions } from "../../hooks/useAppraisalRole";

const CATEGORIES = [
    { value: "performance", label: "Performance" },
    { value: "behavior", label: "Behavior" },
    { value: "discipline", label: "Discipline" },
    { value: "leadership", label: "Leadership" },
    { value: "innovation", label: "Innovation" },
];

const emptyKpi = {
    name: "",
    description: "",
    category: "performance",
    departments: [],            // M2M: empty list ⇒ KPI is universal
    default_weightage: 20,
    is_active: true,
};


/* Master endpoints go through tools.utils.paginator, which returns a bare list
   only when `drop_down` is set and a {result, total_count} object otherwise.
   Ask for the list, and unwrap defensively anyway -- a shape mismatch should
   degrade to an empty dropdown, not take the page down with it. */
const asList = (data) => (Array.isArray(data) ? data : data?.result || []);

const AppraisalKPIManager = () => {
    const { fetch, create, update, remove } = useApiMethods();
    const { canAddKpi, canEditKpi } = useAppraisalPermissions();

    const [kpis, setKpis] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("");

    const [openForm, setOpenForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyKpi);
    const [errors, setErrors] = useState({});
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 350);
        return () => clearTimeout(t);
    }, [search]);

    const loadKpis = useCallback(async () => {
        setLoading(true);
        try {
            const filters = {};
            if (categoryFilter) filters.category = categoryFilter;
            if (departmentFilter) filters.department = departmentFilter;
            if (debouncedSearch) filters.search = debouncedSearch;
            const res = await fetch(APPRAISAL_KPI_METRICS, filters);
            setKpis(res?.data || []);
        } catch (_) {
            ToastMsg("error", "Failed to load KPIs");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryFilter, departmentFilter, debouncedSearch]);

    const loadDepartments = useCallback(async () => {
        try {
            const res = await fetch(EMPLOYEE_DEPARTMENT, { drop_down: "True" });
            setDepartments(asList(res?.data).map((d) => ({ value: d.id, label: d.department_name })));
        } catch (_) { /* non-fatal */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { loadKpis(); }, [loadKpis]);
    useEffect(() => { loadDepartments(); }, [loadDepartments]);

    // Hydrate the form when the dialog opens, normalising the server payload
    // (which exposes `department_ids` / `department_names`) into the form's
    // own `departments` field that the multi-select binds to.
    useEffect(() => {
        if (!openForm) return;
        if (editing) {
            setForm({
                name: editing.name || "",
                description: editing.description || "",
                category: editing.category || "performance",
                departments: Array.isArray(editing.department_ids) ? editing.department_ids : [],
                default_weightage: editing.default_weightage ?? 20,
                is_active: editing.is_active ?? true,
            });
        } else {
            setForm(emptyKpi);
        }
        setErrors({});
    }, [openForm, editing]);

    const handleSave = async () => {
        const e = {};
        if (!form.name?.trim()) e.name = "Name is required.";
        if (form.default_weightage === null || form.default_weightage === undefined || form.default_weightage === "") {
            e.default_weightage = "Weightage is required.";
        } else if (Number(form.default_weightage) < 0 || Number(form.default_weightage) > 100) {
            e.default_weightage = "Must be between 0 and 100.";
        }
        if (Object.keys(e).length > 0) {
            setErrors(e);
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: form.name,
                description: form.description,
                category: form.category,
                departments: form.departments || [],
                default_weightage: Number(form.default_weightage),
                is_active: form.is_active,
            };
            const res = editing
                ? await update(APPRAISAL_KPI_METRICS, { ...payload, id: editing.id })
                : await create(APPRAISAL_KPI_METRICS, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || `KPI ${editing ? "updated" : "created"}`);
                setOpenForm(false);
                setEditing(null);
                loadKpis();
            } else {
                ToastMsg("error", res?.message || "Failed to save KPI");
            }
        } catch (_) {
            ToastMsg("error", "Failed to save KPI");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            const res = await remove(`${APPRAISAL_KPI_METRICS}?id=${deleteTarget.id}`);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "KPI removed");
                setDeleteTarget(null);
                loadKpis();
            } else {
                ToastMsg("error", res?.message || "Failed to remove KPI");
            }
        } catch (_) {
            ToastMsg("error", "Failed to remove KPI");
        }
    };

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={ListChecks}
                title="KPI Manager"
                subtitle="Reusable KPI metrics that can be assigned to employees during a cycle"
                totalRecords={kpis.length}
                buttonEnable={canAddKpi}
                buttonName="Add KPI"
                setOpenDialog={() => { setEditing(null); setOpenForm(true); }}
            />

            <div className="flex flex-wrap items-center gap-2 px-3 pb-3">
                <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search KPI"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="max-w-xs"
                    />
                </div>
                <Select
                    placeholder="Category"
                    allowClear
                    value={categoryFilter || undefined}
                    onChange={(v) => setCategoryFilter(v || "")}
                    options={CATEGORIES}
                    style={{ minWidth: 160 }}
                />
                <Select
                    placeholder="Department"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    value={departmentFilter || undefined}
                    onChange={(v) => setDepartmentFilter(v || "")}
                    options={departments}
                    style={{ minWidth: 180 }}
                />
            </div>

            <div className="px-3 pb-6 overflow-auto">
                {loading ? (
                    <div className="text-center py-10 text-muted-foreground">Loading…</div>
                ) : kpis.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">No KPIs found.</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {kpis.map((k) => {
                            const deptNames = Array.isArray(k.department_names) ? k.department_names : [];
                            return (
                                <div
                                    key={k.id}
                                    className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 flex flex-col gap-2 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">{k.name}</h3>
                                        {canEditKpi && (
                                            <div className="flex gap-1 flex-shrink-0">
                                                <Button variant="ghost" size="icon" onClick={() => { setEditing(k); setOpenForm(true); }} title="Edit">
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(k)} title="Delete" className="text-red-500 hover:text-red-600">
                                                    <Trash className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                    {k.description && (
                                        <p className="text-xs text-muted-foreground line-clamp-2">{k.description}</p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-2 mt-auto pt-2 border-t border-gray-100 dark:border-gray-700">
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-sidebar/10 text-sidebar dark:bg-sidebar-primary/20 dark:text-sidebar-primary">
                                            {k.default_weightage}%
                                        </span>
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 capitalize">
                                            {k.category}
                                        </span>
                                        {deptNames.length > 0 ? (
                                            deptNames.map((name) => (
                                                <span key={name} className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                                    {name}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-xs text-muted-foreground">All departments</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <DialogModal
                open={openForm}
                onClose={() => { setOpenForm(false); setEditing(null); }}
                onSave={handleSave}
                title={editing ? "Edit KPI" : "New KPI"}
                saveLabel={editing ? "Update" : "Create"}
                editMode={!!editing}
                loading={saving}
                width="540px"
            >
                <div className="space-y-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            value={form.name || ""}
                            placeholder="e.g. Code Quality"
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                        {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                        <Textarea
                            rows={3}
                            value={form.description || ""}
                            placeholder="Briefly describe what this KPI measures..."
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                            <Select
                                value={form.category}
                                onChange={(v) => setForm({ ...form, category: v })}
                                options={CATEGORIES}
                                className="w-full"
                                size="large"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Departments (optional)
                            </label>
                            <Select
                                mode="multiple"
                                value={form.departments || []}
                                onChange={(v) => setForm({ ...form, departments: v })}
                                options={departments}
                                className="w-full"
                                size="large"
                                showSearch
                                optionFilterProp="label"
                                placeholder="Leave empty for all departments"
                                maxTagCount="responsive"
                                allowClear
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Default Weightage (%) <span className="text-red-500">*</span>
                        </label>
                        <Input
                            type="number"
                            min={0}
                            max={100}
                            value={form.default_weightage ?? ""}
                            onChange={(e) => setForm({ ...form, default_weightage: e.target.value })}
                        />
                        {errors.default_weightage && <p className="text-xs text-red-500 mt-1">{errors.default_weightage}</p>}
                    </div>
                </div>
            </DialogModal>

            <ConfirmPopupModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Delete KPI"
                description={`Remove "${deleteTarget?.name}" from the KPI library?`}
                type="danger"
                destructive
                confirmLabel="Delete"
            />
        </ContainerCard>
    );
};

export default AppraisalKPIManager;
